import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchContacts, createContact, updateContact /* , deleteContact */ } from '../../redux/contactStore/action';
import { Breadcrumb } from "../../components/Breadcrumb";
import { DataTable } from "../../components/DataTable";
import { Loader } from "../../components/Loader"; // Importing the Loader component
import { Modal } from "../../components/Modal";
import { ContactCreateForm } from "./ContactCreateForm";
import { ContactTypeManager } from './ContactTypeManager';
import Select from 'react-select';
import { LabelManager } from './LabelManager';
import { fetchContactTypes } from '../../redux/contactTypeStore/action';
import { fetchLabels } from '../../redux/labelStore/action';
import { getUserContext } from '../../utils/auth';
import { API } from '../../axios/instance';
import { FileUploadModal } from '../../components/FileUploadModal';
import { uploadFile } from '../../redux/fileStoreStore/action';

const FALLBACK_USER_ID = "6897acb7f2bc1479c645102d"; // Legacy fallback
const DEFAULT_BUSINESS_ID = "6897b362dce30c2c9892b759"; // Legacy fallback

const normalizeContactTypeValues = (value) => {
    if (value === undefined || value === null) return [];
    const entries = Array.isArray(value) ? value : [value];
    const ids = entries
        .map((entry) => {
            if (!entry) return null;
            if (typeof entry === 'string') {
                const trimmed = entry.trim();
                return trimmed === '' ? null : trimmed;
            }
            if (typeof entry === 'object') {
                return entry._id || entry.id || entry.value || null;
            }
            return null;
        })
        .filter(Boolean);
    return Array.from(new Set(ids));
};

const getContactTypeNames = (value, catalog = []) => {
    const ids = normalizeContactTypeValues(value);
    return ids
        .map((id) => catalog.find((t) => t._id === id)?.contactTypeName || null)
        .filter(Boolean);
};

export function ContactMain() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const contacts = useSelector((state) => state.contact.data);
    const loading = useSelector((state) => state.contact.status === "loading");
    // const error = useSelector((state) => state.contact.error);
    const contactTypes = useSelector((state) => state.contactType.data);
    const labels = useSelector((state) => state.label.data);

    const userContext = getUserContext();
    const effectiveUserId = userContext.userId || FALLBACK_USER_ID;
    const effectiveBusinessId = userContext.businessId || DEFAULT_BUSINESS_ID;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isTypeModalVisible, setIsTypeModalVisible] = useState(false);
    const [isLabelModalVisible, setIsLabelModalVisible] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [filterLabel, setFilterLabel] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState(() => ({
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
        contactType: [],
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        referredBy: null,
        referredByName: "",
        referredByPhone: "",
        note: "",
        labels: [],
    }));
    const [page, setPage] = useState(1);
    const limit = 10;
    const [pendingPhotoFiles, setPendingPhotoFiles] = useState([]);
    const [isPhotoModalVisible, setIsPhotoModalVisible] = useState(false);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);

    const handleOpenModal = () => {
        setIsEditing(false);
        const firstTypeId = (Array.isArray(contactTypes) && contactTypes.length > 0) ? contactTypes[0]._id : null;
        setFormData({
            userId: effectiveUserId,
            businessId: effectiveBusinessId,
            contactType: firstTypeId ? [firstTypeId] : [],
            contactName: "",
            contactEmail: "",
            contactPhone: "",
            referredBy: null,
            referredByName: "",
            referredByPhone: "",
            note: "",
            labels: [],
        });
        setIsModalVisible(true);
        setPendingPhotoFiles([]);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setIsEditing(false);
        setPendingPhotoFiles([]);
    };

    useEffect(() => {
        dispatch(fetchContactTypes());
        dispatch(fetchLabels());
    }, [dispatch]);

    useEffect(() => {
        if (isEditing) return;
        if (!contactTypes || contactTypes.length === 0) return;
        setFormData((prev) => {
            const existing = normalizeContactTypeValues(prev.contactType);
            if (existing.length > 0) return prev;
            const fallback = (Array.isArray(contactTypes) && contactTypes.length > 0) ? contactTypes[0]._id : null;
            if (!fallback) return prev;
            return { ...prev, contactType: [fallback] };
        });
    }, [contactTypes, isEditing]);

    useEffect(() => {
        if (isEditing) return;
        setFormData((prev) => {
            if (prev.userId === effectiveUserId) return prev;
            return { ...prev, userId: effectiveUserId };
        });
    }, [effectiveUserId, isEditing]);
    useEffect(() => {
        if (isEditing) return;
        setFormData((prev) => {
            if (prev.businessId === effectiveBusinessId) return prev;
            return { ...prev, businessId: effectiveBusinessId };
        });
    }, [effectiveBusinessId, isEditing]);

    const handleContactSubmit = async (data) => {
        const normalizedTypes = normalizeContactTypeValues(data.contactType);
        if (normalizedTypes.length === 0) {
            alert('Please select at least one contact type.');
            return;
        }
        let referredById = data.referredBy || null;
        try {
            if (!referredById && (data.referredByPhone || '').trim()) {
                referredById = await createReferralContact(data);
            }
        } catch (err) {
            console.error('Failed to create referral contact', err);
            alert('Failed to create the referred by contact. Please try again.');
            return;
        }
        const sanitizedData = {
            ...data,
            referredBy: referredById,
        };
        delete sanitizedData.referredByPhone;
        delete sanitizedData.referredByName;
        const basePayload = {
            ...sanitizedData,
            userId: sanitizedData.userId || effectiveUserId,
            businessId: sanitizedData.businessId || effectiveBusinessId,
            contactType: normalizedTypes,
        };
        try {
            if (isEditing) {
                const payload = {
                    _id: data._id,
                    updatedData: {
                        contactName: data.contactName,
                        contactEmail: data.contactEmail,
                        contactPhone: data.contactPhone,
                        contactType: normalizedTypes,
                        referredBy: data.referredBy ?? null,
                        labels: Array.isArray(data.labels) ? data.labels : [],
                    },
                    note: data.note || "",
                };
                const res = await dispatch(updateContact(payload)).unwrap();
                const updatedId = res?.result?._id || data._id;
                if (updatedId) await uploadPendingPhotos(updatedId);
            } else {
                const res = await dispatch(createContact({ ...basePayload, labels: Array.isArray(basePayload.labels) ? basePayload.labels : [] })).unwrap();
                const createdId = res?.result?._id;
                if (createdId) await uploadPendingPhotos(createdId);
            }
            handleCloseModal();
            dispatch(fetchContacts({
                search: searchTerm || undefined,
                contactType: filterType !== 'all' ? filterType : undefined,
                label: filterLabel !== 'all' ? filterLabel : undefined,
                userId: effectiveUserId,
                businessId: effectiveBusinessId,
            }));
        } catch (error) {
            console.error("Failed to save contact:", error);
        }
    };

    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const uploadPendingPhotos = async (contactId) => {
        if (!pendingPhotoFiles.length) return;
        const files = [...pendingPhotoFiles];
        setIsPhotoUploading(true);
        try {
            for (const file of files) {
                const dataBase64 = await toBase64(file);
                await dispatch(uploadFile({
                    userId: effectiveUserId,
                    businessId: effectiveBusinessId,
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size,
                    dataBase64,
                    contactIds: [contactId],
                })).unwrap();
            }
            setPendingPhotoFiles([]);
        } catch (error) {
            console.error('Failed to upload photos', error);
            alert('Failed to upload photos for this contact.');
        } finally {
            setIsPhotoUploading(false);
        }
    };

    const removePendingPhoto = (idx) => {
        setPendingPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    const createReferralContact = async ({ referredByPhone, referredByName, userId, businessId }) => {
        const phone = (referredByPhone || '').trim();
        if (!phone) return null;
        const name = (referredByName || '').trim();
        const fallbackType = contactTypes?.[0]?._id;
        if (!fallbackType) throw new Error('Please configure a contact type before adding referrers.');
        const payload = {
            userId: userId || effectiveUserId,
            businessId: businessId || effectiveBusinessId,
            contactType: [fallbackType],
            contactName: name || phone,
            contactEmail: '',
            contactPhone: phone,
            referredBy: null,
            labels: [],
        };
        const res = await API.post('contact/contact/create-contact', payload);
        return res?.data?.result?._id || null;
    };

    const handleEdit = (contact) => {
        setFormData({
            _id: contact._id,
            contactName: contact.contactName,
            contactEmail: contact.contactEmail,
            contactPhone: contact.contactPhone,
            userId: contact.userId || effectiveUserId,
            businessId: contact.businessId || effectiveBusinessId,
            contactType: normalizeContactTypeValues(contact.contactType),
            referredBy: (contact.referredBy && (contact.referredBy._id || contact.referredBy)) || null,
            referredByName: "",
            referredByPhone: "",
            labels: Array.isArray(contact.labels) ? contact.labels.map((x)=> (x && x._id) ? x._id : x) : [],
            note: contact.note || "",
        });
        setIsEditing(true);
        setIsModalVisible(true);
        setPendingPhotoFiles([]);
    };

    /* const handleDelete = (contactId) => {
        if (window.confirm("Are you sure you want to delete this contact?")) {
            dispatch(deleteContact(contactId))
                .unwrap()
                .then(() => {
                    console.log("Contact deleted successfully!");
                    dispatch(fetchContacts({
                        search: searchTerm || undefined,
                        contactType: filterType !== 'all' ? filterType : undefined,
                        userId: effectiveUserId,
                        businessId: effectiveBusinessId,
                    }));
                })
                .catch((error) => {
                    console.error("Failed to delete contact:", error);
                });
        }
    }; */

    const columns = [
        {
            Header: "Full Name",
            Cell: (row) => (
                <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0"
                    onClick={() => navigate(`/contact/${row._id}`)}
                    title="View contact details"
                >
                    {row.contactName || '-'}
                </button>
            ),
        },
        {
            Header: "Email",
            accessor: "contactEmail",
        },
        {
            Header: "Phone",
            accessor: "contactPhone",
        },
        {
            Header: "Type",
            Cell: (row) => {
                const names = getContactTypeNames(row.contactType, contactTypes || []);
                if (names.length === 0) return <span className="text-muted">-</span>;
                const first = names[0];
                const more = names.length - 1;
                return (
                    <span className="text-nowrap" title={names.join(', ')}>
                        {first}{more>0 ? ` +${more}` : ''}
                    </span>
                );
            },
        },
        {
            Header: "Labels",
            Cell: (row) => {
                const ids = Array.isArray(row.labels) ? row.labels.map((x)=> (x && x._id) ? x._id : x) : [];
                const names = ids.map(id => labels.find(l=>l._id === id)?.labelName).filter(Boolean);
                if (names.length === 0) return <span className="text-muted">-</span>;
                const first = names[0];
                const more = names.length - 1;
                return (
                  <span className="text-nowrap">
                    {first}{more>0 ? ` +${more}` : ''}
                  </span>
                );
            },
        },
        {
            Header: "Actions",
            Cell: (row) => (
                <div>
                    <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate(`/contact/${row._id}`)}>View</button>
                    <button className="btn btn-sm btn-info" onClick={() => handleEdit(row)}>Edit</button>
                    {/* <button className="btn btn-sm btn-danger ms-2" onClick={() => handleDelete(row._id)}>Delete</button> */}
                </div>
            ),
        },
    ];

    // Server-side fetching when filters, search, or page change
    useEffect(() => {
        if (!effectiveUserId) return;
        const timeout = setTimeout(() => {
            dispatch(fetchContacts({
                page,
                limit,
                search: searchTerm || undefined,
                contactType: filterType !== 'all' ? filterType : undefined,
                label: filterLabel !== 'all' ? filterLabel : undefined,
                userId: effectiveUserId,
                businessId: effectiveBusinessId,
            }));
        }, 300);
        return () => clearTimeout(timeout);
    }, [dispatch, filterType, filterLabel, searchTerm, page, limit, effectiveUserId, effectiveBusinessId]);

    // Reset to page 1 when filters/search change
    useEffect(() => {
        setPage(1);
    }, [filterType, filterLabel, searchTerm]);

    return (
        <Fragment>
            <div className="container">
                <div className="d-flex align-items-center justify-content-between">
                    <Breadcrumb
                        items={[
                            { label: "Home", path: "/dashboard" },
                            { label: "Contacts", path: "/contact" },
                            { label: "Clients" }
                        ]}
                    />
                    {loading ? <Loader size="sm" /> : null}
                </div>
                {/* {error && <p style={{ color: 'red' }}>Error: {error}</p>} */}

                <div className="row align-items-end g-2 mb-3">
                    <div className="col-md-8">
                        <div className="d-flex flex-column flex-sm-row gap-2">
                            <div className="flex-grow-1">
                                <label htmlFor="search" className="visually-hidden">Search</label>
                                <input
                                    id="search"
                                    className="form-control"
                                    placeholder="Search name, email, or phone"
                                    value={searchTerm}
                                    onChange={(e)=> setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div style={{ minWidth: 160 }}>
                                <label htmlFor="filterType" className="visually-hidden">Filter by Type</label>
                                <Select
                                    inputId="filterType"
                                    classNamePrefix="rs"
                                    options={[{ value: 'all', label: 'All types' }, ...(contactTypes||[]).map(t=>({ value: t._id, label: t.contactTypeName }))]}
                                    value={(contactTypes||[]).map(t=>({ value: t._id, label: t.contactTypeName })).concat([{value:'all',label:'All types'}]).find(o=>o.value===filterType) || {value:'all',label:'All types'}}
                                    onChange={(opt)=> setFilterType(opt?.value || 'all')}
                                />
                            </div>
                            <div style={{ minWidth: 160 }}>
                                <label htmlFor="filterLabel" className="visually-hidden">Filter by Label</label>
                                <Select
                                    inputId="filterLabel"
                                    classNamePrefix="rs"
                                    options={[{ value: 'all', label: 'All labels' }, ...(labels||[]).map(l=>({ value: l._id, label: l.labelName }))]}
                                    value={(labels||[]).map(l=>({ value: l._id, label: l.labelName })).concat([{value:'all',label:'All labels'}]).find(o=>o.value===filterLabel) || {value:'all',label:'All labels'}}
                                    onChange={(opt)=> setFilterLabel(opt?.value || 'all')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="d-flex justify-content-md-end gap-2">
                            <button className="btn btn-primary" onClick={handleOpenModal}>
                                Add Contact
                            </button>
                            <button className="btn btn-outline-secondary" onClick={()=> setIsTypeModalVisible(true)}>
                                Types
                            </button>
                            <button className="btn btn-outline-secondary" onClick={()=> setIsLabelModalVisible(true)}>
                                Labels
                            </button>
                        </div>
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={contacts}
                    disablePagination={true}
                />

                {/* Simple server-side pagination controls */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted small">Page {page}</div>
                    <div className="btn-group">
                        <button className="btn btn-outline-secondary btn-sm" disabled={page<=1 || loading} onClick={()=> setPage(p=>Math.max(1,p-1))}>Prev</button>
                        <button className="btn btn-outline-secondary btn-sm" disabled={(contacts?.length||0) < limit || loading} onClick={()=> setPage(p=>p+1)}>Next</button>
                    </div>
                </div>

                <Modal
                    show={isModalVisible}
                    onClose={handleCloseModal}
                    title={isEditing ? "Edit Contact" : "Create Contact"}
                    body={
                        <ContactCreateForm
                            formData={formData}
                            setFormData={setFormData}
                            contactTypes={contactTypes}
                            allContacts={contacts}
                            onManageTypes={()=> setIsTypeModalVisible(true)}
                            labelsList={labels}
                            onManageLabels={()=> setIsLabelModalVisible(true)}
                            pendingPhotos={pendingPhotoFiles}
                            onOpenPhotoModal={()=> setIsPhotoModalVisible(true)}
                            onRemovePhoto={removePendingPhoto}
                        />
                    }
                    footer={
                        <>
                            <button className="btn btn-secondary" onClick={handleCloseModal}>
                                Close
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => handleContactSubmit(formData)}
                                disabled={isPhotoUploading}
                            >
                                {isEditing ? "Update Changes" : "Save Changes"}
                            </button>
                        </>
                    }
                />
            </div>
            <Modal
                show={isTypeModalVisible}
                onClose={()=> setIsTypeModalVisible(false)}
                title="Manage Contact Types"
                body={<ContactTypeManager />}
                footer={<button className="btn btn-secondary" onClick={()=> setIsTypeModalVisible(false)}>Close</button>}
            />
            <Modal
                show={isLabelModalVisible}
                onClose={()=> setIsLabelModalVisible(false)}
                title="Manage Labels"
                body={<LabelManager />}
                footer={<button className="btn btn-secondary" onClick={()=> setIsLabelModalVisible(false)}>Close</button>}
            />
            <FileUploadModal
                show={isPhotoModalVisible}
                onClose={()=> setIsPhotoModalVisible(false)}
                title="Select Photos"
                helperText="Choose one or more images to attach to this contact."
                accept="image/*"
                selectedFiles={pendingPhotoFiles}
                onFilesChange={setPendingPhotoFiles}
                primaryActionLabel="Done"
                primaryDisabled={false}
                onPrimaryAction={()=> setIsPhotoModalVisible(false)}
            >
                <div className="form-text">Selected photos upload after saving the contact.</div>
            </FileUploadModal>
        </Fragment>
    );
}
